import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import Button from "@/components/base/Button";
import { Textarea } from "@/components/base/Field";
import { useToast } from "@/components/base/Toast";

interface Props {
  open: boolean;
  title: string;
  subtitle?: string;
  /** Explication affichée au-dessus du motif. */
  message: string;
  confirmLabel: string;
  onClose: () => void;
  /** Reçoit le motif ; une erreur levée est affichée et la fenêtre reste ouverte. */
  onConfirm: (reason: string) => Promise<void>;
}

/** Action sensible qui exige un motif écrit (réouverture d'un mois ou d'une caisse). */
export default function ReasonModal({ open, title, subtitle, message, confirmLabel, onClose, onConfirm }: Props) {
  const { notify } = useToast();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  const confirm = async () => {
    if (reason.trim().length < 5) {
      notify("Expliquez le motif (5 caractères minimum).", "error");
      return;
    }
    setBusy(true);
    try {
      await onConfirm(reason.trim());
      onClose();
    } catch (err) {
      notify(err instanceof Error ? err.message : "Opération impossible.", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      icon="ri-lock-unlock-line"
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button variant="danger" icon="ri-lock-unlock-line" onClick={confirm} disabled={busy}>
            {busy ? "Enregistrement..." : confirmLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-foreground-600">{message}</p>
        <Textarea
          label="Motif (obligatoire)"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Ex. : recette de 150 000 oubliée le 12"
        />
        <p className="text-xs text-foreground-500">Le motif est conservé dans l'historique et le journal des actions.</p>
      </div>
    </Modal>
  );
}
